import * as React from "react";
import styled, { css } from "styled-components";

import { Text } from "src/library/components/Text/Text";
import { useCurrentUser } from "src/core/users/hooks";
import { IRole } from "src/core/shared/api/roles.api";

import { SupportTicketStatus } from "../api/support.api";

interface IProps {
  status: SupportTicketStatus;
}

interface IStatusWrapper {
  status: SupportTicketStatus;
  role?: IRole["type"];
}

const StatusWrapper = styled(Text)<IStatusWrapper>`
  font-weight: ${({ theme }) => theme.fonts.weights.bold};
  color: ${({ theme }) => theme.colors.darkGrey};
  ${({ status, role }) => StatusCss(status, role)};
`;

const DefaultCss = css`
  color: inherit;
`;

const ActiveCss = css`
  color: ${({ theme }) => theme.colors.success};
`;

const PendingCss = css`
  color: ${({ theme }) => theme.colors.warn};
`;

const NewCss = css`
  color: ${({ theme }) => theme.colors.error}
`;

// Gives the status the appropriate colour
const StatusCss = (status: SupportTicketStatus, role?: IRole["type"]) => {
  switch (status) {
    case SupportTicketStatus.AdminResolved:
    case SupportTicketStatus.CustomerResolved:
      return ActiveCss;
    case SupportTicketStatus.PendingAdminResponse:
      return role === "admin" ? PendingCss : DefaultCss;
    case SupportTicketStatus.PendingCustomerResponse:
      return role === "customer" ? PendingCss : DefaultCss;

    case SupportTicketStatus.New:
      return role === "admin" ? NewCss : DefaultCss;

    default:
      return DefaultCss;
  }
};

export const statusTextSwitch = (status: SupportTicketStatus, currentRoleType?: string): string => {
  switch (status) {
    case SupportTicketStatus.New:
      return currentRoleType === "admin" ? "New" : "Awaiting reply";

    case SupportTicketStatus.PendingAdminResponse:
      return currentRoleType === "admin" ? "Awaiting your reply" : "Awaiting reply";

    case SupportTicketStatus.PendingCustomerResponse:
      return currentRoleType === "customer" ? "Awaiting your reply" : "Awaiting reply";

    case SupportTicketStatus.AdminResolved:
    case SupportTicketStatus.CustomerResolved:
      return "Resolved";

    default:
      return "";
  }
};

/**
 * Gets the support ticket status styled
 */
const SupportTicketStatusStyled: React.ReactFCWithChildren<IProps> = ({ status }) => {
  const [ { currentRole } ] = useCurrentUser();

  const statusText = React.useMemo(() => {
    return statusTextSwitch(status, currentRole?.type);
  }, [ currentRole, status ]);

  return (
    <StatusWrapper
      status={status}
      role={currentRole?.type}
    >
      {statusText}
    </StatusWrapper>
  );
};

export default SupportTicketStatusStyled;
